import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadServiceConstants, updateServiceConstants } from '../../../store/participantService';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    ADDRESS_LINE_4_MAX_LENGTH,
    CITY_MAX_LENGTH,
    COUNTY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_PHONE,
    MAX_POSTCODE
} from '../../../utils/formValidation/commonConstants';
import { hasRole, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import TextInputField from '../../formElements/TextInputField';

import { validate } from './validateAddressAndContactInfo';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const AddressAndContactInfo = ({ serviceId }) => {
    const dispatch = useDispatch();

    const initialContactInfo = {
        formAddressLine1: '',
        formAddressLine2: '',
        formAddressLine3: '',
        formAddressLine4: '',
        formCity: '',
        formCounty: '',
        formPostcode: '',
        formPhoneNumber: '',
        exitFormEmailAddress: '',
        sanctionFormFromEmailAddress: '',
        alternativeFromEmailAddress: '',
        serviceAddressLine1: '',
        serviceAddressLine2: '',
        serviceAddressLine3: '',
        serviceAddressLine4: '',
        serviceCity: '',
        serviceCounty: '',
        servicePostcode: ''
    };

    const [newEntry, setNewEntry] = useState(initialContactInfo);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialContactInfo);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const serviceConstants = useSelector(
        (state) => state.entities.participantService.serviceConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    useEffect(() => {
        if (serviceConstants[serviceId]) {
            setNewEntry(serviceConstants[serviceId]);
            setInitialStateOnEntry(serviceConstants[serviceId]);
        } else dispatch(loadServiceConstants(serviceId));
    }, [serviceId, serviceConstants]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes('Service constants has been updated') || errorMessage) {
            setIsButtonDisabled(false);
        }
    }, [successMessage, errorMessage]);

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry({ ...newEntry, [key]: value });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        dispatch(updateServiceConstants(newEntry));
    };

    if (!hasRole([SUPERUSER], roles)) return;

    return (
        <form onSubmit={onSubmit} data-testid="form_start" className={form.formWrapper}>
            <div className={app.sectionHeading}>Form Details</div>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <TextInputField
                        id={`${serviceId}-formAddressLine1`}
                        label={'Form Address Line 1'}
                        placeholder={'Enter form address line 1'}
                        maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                        value={newEntry.formAddressLine1 || ''}
                        error={errors.formAddressLine1}
                        onChange={(e) => onUpdate('formAddressLine1', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formAddressLine2`}
                        label={'Form Address Line 2'}
                        placeholder={'Enter form address line 2'}
                        maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                        value={newEntry.formAddressLine2 || ''}
                        error={errors.formAddressLine2}
                        onChange={(e) => onUpdate('formAddressLine2', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formAddressLine3`}
                        label={'Form Address Line 3'}
                        placeholder={'Enter form address line 3'}
                        maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                        value={newEntry.formAddressLine3 || ''}
                        error={errors.formAddressLine3}
                        onChange={(e) => onUpdate('formAddressLine3', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formAddressLine4`}
                        label={'Form Address Line 4'}
                        placeholder={'Enter form address line 4'}
                        maxLength={ADDRESS_LINE_4_MAX_LENGTH}
                        value={newEntry.formAddressLine4 || ''}
                        error={errors.formAddressLine4}
                        onChange={(e) => onUpdate('formAddressLine4', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formCity`}
                        label={'Form City'}
                        placeholder={'Enter form city'}
                        maxLength={CITY_MAX_LENGTH}
                        value={newEntry.formCity || ''}
                        error={errors.formCity}
                        onChange={(e) => onUpdate('formCity', e.target.value)}
                    />
                </div>
                <div className={form.formColumn}>
                    <TextInputField
                        id={`${serviceId}-formCounty`}
                        label={'Form County'}
                        placeholder={'Enter form county'}
                        maxLength={COUNTY_MAX_LENGTH}
                        value={newEntry.formCounty || ''}
                        error={errors.formCounty}
                        onChange={(e) => onUpdate('formCounty', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formPostcode`}
                        label={'Form Postcode'}
                        placeholder={'Enter form postcode'}
                        maxLength={MAX_POSTCODE}
                        value={newEntry.formPostcode || ''}
                        error={errors.formPostcode}
                        onChange={(e) => onUpdate('formPostcode', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-formPhoneNumber`}
                        label={'Form Phone Number'}
                        placeholder={'Enter form phone number'}
                        maxLength={MAX_PHONE}
                        value={newEntry.formPhoneNumber || ''}
                        error={errors.formPhoneNumber}
                        onChange={(e) => onUpdate('formPhoneNumber', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-exitFormEmailAddress`}
                        label={'Exit Form From Email Address'}
                        placeholder={'Enter exit form from email address'}
                        maxLength={MAX_EMAIL}
                        value={newEntry.exitFormEmailAddress || ''}
                        error={errors.exitFormEmailAddress}
                        onChange={(e) => onUpdate('exitFormEmailAddress', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-sanctionFormFromEmailAddress`}
                        label={'Sanction Form From Email Address'}
                        placeholder={'Enter sanction form from email address'}
                        maxLength={MAX_EMAIL}
                        value={newEntry.sanctionFormFromEmailAddress || ''}
                        error={errors.sanctionFormFromEmailAddress}
                        onChange={(e) => onUpdate('sanctionFormFromEmailAddress', e.target.value)}
                    />
                </div>
            </div>
            <div className={app.sectionHeading}>Service Details</div>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <TextInputField
                        id={`${serviceId}-serviceAddressLine1`}
                        label={'Service Address Line 1'}
                        placeholder={'Enter service address line 1'}
                        maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                        value={newEntry.serviceAddressLine1 || ''}
                        error={errors.serviceAddressLine1}
                        onChange={(e) => onUpdate('serviceAddressLine1', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-serviceAddressLine2`}
                        label={'Service Address Line 2'}
                        placeholder={'Enter service address line 2'}
                        maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                        value={newEntry.serviceAddressLine2 || ''}
                        error={errors.serviceAddressLine2}
                        onChange={(e) => onUpdate('serviceAddressLine2', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-serviceAddressLine3`}
                        label={'Service Address Line 3'}
                        placeholder={'Enter service address line 3'}
                        maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                        value={newEntry.serviceAddressLine3 || ''}
                        error={errors.serviceAddressLine3}
                        onChange={(e) => onUpdate('serviceAddressLine3', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-serviceAddressLine4`}
                        label={'Service Address Line 4'}
                        placeholder={'Enter service address line 4'}
                        maxLength={ADDRESS_LINE_4_MAX_LENGTH}
                        value={newEntry.serviceAddressLine4 || ''}
                        error={errors.serviceAddressLine4}
                        onChange={(e) => onUpdate('serviceAddressLine4', e.target.value)}
                    />
                </div>
                <div className={form.formColumn}>
                    <TextInputField
                        id={`${serviceId}-serviceCity`}
                        label={'Service City'}
                        placeholder={'Enter service city'}
                        maxLength={CITY_MAX_LENGTH}
                        value={newEntry.serviceCity || ''}
                        error={errors.serviceCity}
                        onChange={(e) => onUpdate('serviceCity', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-serviceCounty`}
                        label={'Service County'}
                        placeholder={'Enter service county'}
                        maxLength={COUNTY_MAX_LENGTH}
                        value={newEntry.serviceCounty || ''}
                        error={errors.serviceCounty}
                        onChange={(e) => onUpdate('serviceCounty', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-servicePostcode`}
                        label={'Service Postcode'}
                        placeholder={'Enter service postcode'}
                        maxLength={MAX_POSTCODE}
                        value={newEntry.servicePostcode || ''}
                        error={errors.servicePostcode}
                        onChange={(e) => onUpdate('servicePostcode', e.target.value)}
                    />
                    <TextInputField
                        id={`${serviceId}-alternativeFromEmailAddress`}
                        label={'Alternative From Email Address'}
                        placeholder={'Enter an alternative from email address'}
                        maxLength={MAX_EMAIL}
                        value={newEntry.alternativeFromEmailAddress || ''}
                        error={errors.alternativeFromEmailAddress}
                        onChange={(e) => onUpdate('alternativeFromEmailAddress', e.target.value)}
                    />
                </div>
            </div>
            <Button
                id="updateAddressAndContactInfoButton"
                content="UPDATE"
                disabled={isButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

AddressAndContactInfo.propTypes = {
    serviceId: PropTypes.string
};

export default AddressAndContactInfo;
